import { useRef, useState } from 'react';
import { Button } from '@/ui';
import { templatesApi } from '@/api/templates';
import { StepLayout } from '../StepLayout';

interface SpectoraStepProps {
  onDone: () => void;
  onSkip: () => void;
}

interface ImportSummary {
  templateName: string;
  sections: number;
  components: number;
  defects: number;
  deficienciesInserted: number;
}

const ACCEPT = '.xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel';

export function SpectoraStep({ onDone, onSkip }: SpectoraStepProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [summary, setSummary] = useState<ImportSummary | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handlePick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setError(null);
    setSummary(null);
  };

  const handleImport = async () => {
    if (!file) return;
    setImporting(true);
    setError(null);
    try {
      const result = await templatesApi.importSpectora(file);
      setSummary({
        templateName: result.template.name,
        sections: result.counts.sections,
        components: result.counts.components,
        defects: result.counts.defects,
        deficienciesInserted: result.counts.deficienciesInserted,
      });
    } catch (err) {
      // The backend returns a 400 with a useful message for SpectoraParseError;
      // surface that verbatim. Anything else collapses to a generic message
      // because the underlying error is unlikely to mean anything to the user.
      const resp = (err as { response?: { data?: { error?: string } } })?.response;
      const backendMsg = typeof resp?.data?.error === 'string' ? resp.data.error : null;
      setError(backendMsg || 'Could not import that file. Make sure it\'s a Spectora template export (.xlsx).');
    } finally {
      setImporting(false);
    }
  };

  // After a successful import, "Continue" advances the wizard. Before import,
  // it kicks off the upload (or, with no file, just skips).
  const primary = summary
    ? { label: 'Continue', onClick: onDone }
    : file
      ? { label: 'Import template', onClick: handleImport }
      : { label: 'Continue', onClick: onSkip };

  return (
    <StepLayout
      title="Coming from Spectora?"
      subtitle="Upload an XLSX export and we'll rebuild your sections, components, and deficiency library here. Starting fresh? Skip this step — your account is already set up with a default template and deficiency library."
      onPrimary={primary.onClick}
      primaryLabel={primary.label}
      primaryLoading={importing}
      onSecondary={summary ? undefined : onSkip}
      secondaryLabel={summary ? undefined : "Skip — I'm starting fresh"}
      error={error}
      notice={summary
        ? `Imported "${summary.templateName}" — ${summary.sections} sections, ${summary.components} components, ${summary.deficienciesInserted} library entries.`
        : null}
    >
      <div className="flex flex-col items-center gap-4 py-6 rounded-xl border-2 border-dashed border-border-strong bg-surface-2">
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-ink-subtle">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="17 8 12 3 7 8" />
          <line x1="12" y1="3" x2="12" y2="15" />
        </svg>
        <div className="text-center">
          <p className="text-body-md text-ink font-medium">
            {file ? file.name : 'Choose a Spectora export'}
          </p>
          <p className="text-caption text-ink-subtle mt-1">
            {file ? `${(file.size / 1024).toFixed(0)} KB` : 'Export from Spectora → Templates → Download'}
          </p>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPT}
          onChange={handlePick}
          className="hidden"
        />
        <Button variant="secondary" onClick={() => fileInputRef.current?.click()} disabled={importing}>
          {file ? 'Choose a different file' : 'Browse files'}
        </Button>
      </div>
    </StepLayout>
  );
}
